import { Page } from "@components/layout/Page";
import { EnquiryForm } from "@components/shared/EnquiryForm";
import { Section } from "@components/shared/Section";
import { LicenceLine } from "@components/shared/LicenceLine";
import { CONTACT } from "@/lib/site";
import { useTranslations } from "@services/strings";
import { Mail, MapPin, Phone } from "lucide-react";

const ContactPage = () => {
  const { t } = useTranslations();

  const channels = [
    {
      icon: Phone,
      label: t("site.contact.phone"),
      value: CONTACT.phoneDisplay,
      href: `tel:${CONTACT.phone}`,
      note: t("site.contact.hours"),
    },
    {
      icon: Mail,
      label: t("site.contact.email"),
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
    },
    {
      icon: MapPin,
      label: t("site.contact.office"),
      value: t("site.footer.registeredAddress"),
      note: t("site.contact.directions"),
      noteHref: CONTACT.mapsUrl,
    },
  ];

  return (
    <Page
      title={t("site.contact.meta.title")}
      description={t("site.contact.meta.description")}
    >
      <Section tone="white">
        <div className="grid gap-12 md:grid-cols-[0.9fr_1.1fr] md:gap-16">
          <div>
            <h1 className="display text-display-sm md:text-h2">
              {t("site.contact.title")}
            </h1>
            <p className="mt-4 max-w-prose text-body text-slate-600">
              {t("site.contact.subtitle")}
            </p>

            <dl className="mt-10 flex flex-col divide-y divide-slate-200">
              {channels.map(
                ({ icon: Icon, label, value, href, note, noteHref }) => (
                  <div key={label} className="flex gap-4 py-5 first:pt-0">
                    <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-mist text-ink">
                      <Icon size={18} aria-hidden="true" />
                    </span>
                    <div>
                      <dt className="text-sm text-slate-500">{label}</dt>
                      <dd className="mt-0.5 text-body font-semibold text-ink">
                        {href ? (
                          <a href={href} className="hover:underline" dir="ltr">
                            {value}
                          </a>
                        ) : (
                          value
                        )}
                      </dd>
                      {note ? (
                        <dd className="text-sm text-slate-500">
                          {noteHref ? (
                            <a
                              href={noteHref}
                              target="_blank"
                              rel="noreferrer"
                              className="underline decoration-1 underline-offset-4 hover:text-ink"
                            >
                              {note}
                            </a>
                          ) : (
                            note
                          )}
                        </dd>
                      ) : null}
                    </div>
                  </div>
                ),
              )}
            </dl>

            <div className="mt-10 rounded-2xl bg-mist p-5 text-sm text-slate-600">
              <p className="font-semibold text-ink">
                {t("site.contact.legalTitle")}
              </p>
              <LicenceLine className="mt-1" />
            </div>
          </div>

          <div className="rounded-3xl bg-mist p-6 md:p-8" id="contact-form">
            <h2 className="text-h3 font-semibold">
              {t("site.contact.form.title")}
            </h2>
            <p className="mt-2 text-body text-slate-600">
              {t("site.contact.form.body")}
            </p>
            <EnquiryForm
              defaultInterest="unlock"
              placement="contact"
              className="mt-6"
            />
          </div>
        </div>
      </Section>
    </Page>
  );
};

export default ContactPage;
